/**
 * Composable: useClinicTimezone
 * Goal: Provide clinic timezone and consistent "absolute (clinic-local)" time formatting
 * Notes:
 * - Backend slot_start_ts currently encodes the clinic's wall time using UTC fields.
 *   To render the intended clinic-local time regardless of viewer location, we
 *   format using UTC getters (getUTCHours/getUTCMinutes/etc.).
 * - When backend becomes real IANA-tz aware for slots, we can switch to Intl API
 *   with timeZone options here without touching call sites.
 */
export default function useClinicTimezone() {
  const { doctorDetails } = useSearch()

  const clinicTimezone = computed<string>(() => {
    // Prefer facility business_hours.timezone if present
    const tz = doctorDetails.value?.facilities?.[0]?.business_hours?.timezone
    return typeof tz === 'string' && tz.trim() ? tz : 'America/New_York'
  })

  const formatClinicTimeFromISO = (iso: string): string => {
    if (!iso) return ''
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ''
    let hours = d.getUTCHours()
    const minutes = d.getUTCMinutes()
    const isPM = hours >= 12
    const displayHour = (hours % 12) || 12
    const mm = minutes < 10 ? `0${minutes}` : `${minutes}`
    const ampm = isPM ? '下午' : '上午'
    return `${ampm} ${displayHour}:${mm}`
  }

  const formatClinicDateFromISO = (iso: string): string => {
    if (!iso) return ''
    const d = new Date(iso)
    if (isNaN(d.getTime())) return ''
    const y = d.getUTCFullYear()
    const m = (d.getUTCMonth() + 1).toString().padStart(2, '0')
    const day = d.getUTCDate().toString().padStart(2, '0')
    return `${y}-${m}-${day}`
  }

  const formatClinicDateTimeFromISO = (iso: string): string => {
    const date = formatClinicDateFromISO(iso)
    const time = formatClinicTimeFromISO(iso)
    if (!date && !time) return ''
    if (!date) return time
    if (!time) return date
    return `${date} ${time}`
  }

  return {
    clinicTimezone,
    formatClinicTimeFromISO,
    formatClinicDateFromISO,
    formatClinicDateTimeFromISO,
  }
}

